import { useEffect, useState, useRef } from 'preact/hooks';
import { searchBible, type SearchHit } from '../audio/biblebrain.js';
import { currentBible } from '../state/bible-store.js';
import { route } from '../state/store.js';

type Props = {
  onClose: () => void;
};

/**
 * Slide-up search panel. Queries Bible Brain's `/search` endpoint for
 * the current bible's text filesets. Tapping a hit jumps the reader to
 * the verse's chapter (and we leave verse-level scrolling to the
 * auto-scroll-to-karaoke effect; for now, route just to the chapter).
 */
export function SearchPanel({ onClose }: Props) {
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<SearchHit[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setHits(null);
      setError(null);
      return;
    }
    let alive = true;
    setLoading(true);
    setError(null);
    const debounce = setTimeout(async () => {
      try {
        const results = await searchBible(q, currentBible.peek().bibleId, { limit: 40 });
        if (!alive) return;
        setHits(results);
        if (results.length === 0) {
          setError(`No matches for "${q}".`);
        }
      } catch (err) {
        if (!alive) return;
        setError(err instanceof Error ? err.message : 'Search failed.');
        setHits(null);
      } finally {
        if (alive) setLoading(false);
      }
    }, 280);
    return () => {
      alive = false;
      clearTimeout(debounce);
    };
  }, [query]);

  const onPick = (hit: SearchHit): void => {
    route.value = { book: hit.book, chapter: hit.chapter };
    onClose();
  };

  return (
    <div class="search-panel-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div class="search-panel" onClick={(e) => e.stopPropagation()}>
        <header class="search-panel-header">
          <input
            ref={inputRef}
            type="search"
            class="search-panel-input"
            value={query}
            placeholder="Search scripture…"
            onInput={(e) => setQuery((e.currentTarget as HTMLInputElement).value)}
            spellcheck={false}
            autocomplete="off"
          />
          <button
            type="button"
            class="search-panel-close"
            onClick={onClose}
            aria-label="Close search"
          >
            ×
          </button>
        </header>
        <div class="search-panel-body">
          {loading ? <p class="search-panel-status">Searching…</p> : null}
          {!loading && error ? <p class="search-panel-status">{error}</p> : null}
          {!loading && hits && hits.length > 0 ? (
            <ul class="search-panel-list">
              {hits.map((hit) => (
                <li key={hit.ref}>
                  <button
                    type="button"
                    class="search-panel-hit"
                    onClick={() => onPick(hit)}
                  >
                    <span class="search-panel-hit-ref">{hit.ref.replace(/\./g, ' ')}</span>
                    <span class="search-panel-hit-text">{hit.text}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {!loading && !hits && !error ? (
            <p class="search-panel-hint">
              Type at least 2 characters to search the whole bible.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
